<template>
  <div>
    <BModal
      :id="id"
      v-model="show"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      hide-header
      hide-footer
      centered
      no-fade
      no-header
      body-class="p-0"
    >
      <div>
        <!-- 初期表示ページ -->
        <ModalPage
          v-if="!isClickedNotSatisfied && !isClickedSatisfied"
          acceptText="満足"
          declineText="不満"
          acceptButtonVariant="secondary"
          @accept="onClickSatisfied"
          @decline="onClickNotSatisfied"
        >
          <template v-slot:description>
            <img
              src="@/assets/img/AppSatisfactionModal/InitialPage.png"
              class="modal-body-image"
            />
          </template>
        </ModalPage>

        <!-- 満足を選択した場合のページ -->
        <ModalPage
          v-else-if="isClickedSatisfied"
          acceptText="評価する"
          declineText="あとで"
          acceptButtonVariant="secondary"
          @accept="onClickCheering"
          @decline="onClickSatisfiedAndAfterSoon"
        >
          <template v-slot:description>
            <img
              src="@/assets/img/AppSatisfactionModal/SatisfiedPage.png"
              class="modal-body-image"
            />
          </template>
        </ModalPage>

        <!-- 不満を選択した場合のページ -->
        <ModalPage
          v-else
          acceptText="コメントする"
          declineText="あとで"
          @accept="onClickToRequest"
          @decline="onClickNotSatisfiedAndAfterSoon"
        >
          <template v-slot:description>
            <img
              src="@/assets/img/AppSatisfactionModal/NotSatisfiedPage.png"
              class="modal-body-image"
            />
          </template>
        </ModalPage>
      </div>
    </BModal>
  </div>
</template>

<script lang="ts">
import { BModal } from "bootstrap-vue";
import { Component, Prop, Vue } from "vue-facing-decorator";
import ModalPage from "./ModalPage.vue";
import { accountService } from "@/app/services/AccountService";
import { captureException } from "@/plugins/sentry";
import { deviceModule } from "@/store/device";
import { openInNewWindow } from "@/utils/browserUtil";
import { authStore } from "@/store/auth";

// アプリ評価を促すモーダル
@Component({
  components: {
    BModal,
    ModalPage
  }
})
export default class AppSatisfactionModal extends Vue {
  @Prop({
    required: true,
    type: String
  })
  id!: string;

  @Prop({
    required: true,
    type: Boolean,
    default: false
  })
  shouldShow!: boolean;

  @Prop({
    required: false,
    type: String
  })
  answeredTiming!: string;

  /**
   * 不満ボタンが押されたかどうか
   */
  isClickedNotSatisfied = false;

  /**
   * 満足ボタンが押されたかどうか
   */
  isClickedSatisfied = false;

  /**
   * アプリストアのリンク
   */
  appStoreLink?: string;

  /**
   * モーダルのタイトル
   */
  get modalTitle() {
    if (this.isClickedSatisfied) {
      // 満足を選択した場合
      return "カイテクを評価して応援してください！🤝✨";
    } else if (this.isClickedNotSatisfied) {
      // 不満を選択した場合
      return "評価ありがとうございます";
    }
    // 初期表示の場合
    return "カイテクの使い心地はいかがですか？";
  }

  /**
   * モーダルを表示するかどうか
   * APIで表示すると判定された場合、且つ、モバイルアプリの場合に表示する
   */
  get show() {
    return (
      this.shouldShow && !deviceModule.isBrowser && this.answeredTiming !== null
    );
  }

  created() {
    if (deviceModule.isIos) {
      this.appStoreLink = authStore.getPlatformStoreUrl("iOS", {
        showReview: true
      });
    } else if (deviceModule.isAndroid) {
      this.appStoreLink = authStore.getPlatformStoreUrl("Android");
    }
  }

  /**
   * 満足ボタンが押された時の処理
   */
  onClickSatisfied() {
    this.isClickedSatisfied = true;
  }

  /**
   * 不満ボタンが押された時の処理
   */
  onClickNotSatisfied() {
    this.isClickedNotSatisfied = true;
  }

  /**
   * 「応援する」ボタンが押された時の処理
   */
  async onClickCheering() {
    try {
      this.$loading.show();
      await accountService.submitAppReview({
        satisfactionType: "satisfied_and_reviewed",
        answeredTiming: this.answeredTiming
      });
    } catch (e) {
      captureException("アプリ評価の送信に失敗しました" + JSON.stringify(e));
    } finally {
      this.$loading.hide();
    }
    openInNewWindow(this.appStoreLink as string);
    this.$emit("close");
  }

  /**
   * 「改善要望」ボタンが押された時の処理
   */
  async onClickToRequest() {
    try {
      this.$loading.show();
      await accountService.submitAppReview({
        satisfactionType: "not_satisfied",
        answeredTiming: this.answeredTiming
      });
    } catch (e) {
      captureException("アプリ評価の送信に失敗しました" + JSON.stringify(e));
    } finally {
      this.$loading.hide();
    }
    openInNewWindow(process.env.VUE_APP_URL_CONTACT!);
    this.$emit("close");
  }

  /**
   * 「満足」>「あとで」ボタンが押されたときの処理
   */
  async onClickSatisfiedAndAfterSoon() {
    try {
      this.$loading.show();
      await accountService.submitAppReview({
        satisfactionType: "satisfied_and_not_reviewed",
        answeredTiming: this.answeredTiming
      });
    } catch (e) {
      captureException("アプリ評価の送信に失敗しました" + JSON.stringify(e));
    } finally {
      this.$loading.hide();
    }
    this.$emit("close");
  }

  /**
   * 「不満」>「あとで」ボタンが押されたときの処理
   */
  async onClickNotSatisfiedAndAfterSoon() {
    try {
      this.$loading.show();
      await accountService.submitAppReview({
        satisfactionType: "not_satisfied",
        answeredTiming: this.answeredTiming
      });
    } catch (e) {
      captureException("アプリ評価の送信に失敗しました" + JSON.stringify(e));
    } finally {
      this.$loading.hide();
    }
    this.$emit("close");
  }
}
</script>

<style lang="scss" scoped>
.modal-body-image {
  width: 100%;
}
</style>
